import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { diagnoseResults } from '../data/diagnosisLogic';
import { crmData } from '../data/crmData';

export const AIDiagnosis = () => {
  const [selectedProject, setSelectedProject] = useState(crmData.projects[0]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  // Simulerte data fra kartlegging (Input Data)
  const mockMetrics = {
    leadershipScore: 48,
    workloadScore: 35,
    psychSafety: 42,
    conflictScore: 65,
    collaborationScore: 58,
    stressScore: 78,
    roleClarityScore: 62,
    cultureScore: 55
  };

  const runAnalysis = () => {
    setIsAnalyzing(true);
    setDiagnosis(null);
    setTimeout(() => {
      setDiagnosis(diagnoseResults(mockMetrics));
      setIsAnalyzing(false);
    }, 2000);
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'Critical': return 'text-rose-600 bg-rose-50 border-rose-200';
      case 'High': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Moderate': return 'text-amber-600 bg-amber-50 border-amber-200';
      default: return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Arbeidsmiljødiagnose</h1>
          <p className="text-slate-500 mt-1">Avansert analyse av kartleggingsdata og generering av tiltaksplan.</p>
        </div>
        <div className="flex gap-3">
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20"
            onChange={(e) => setSelectedProject(crmData.projects.find(p => p.id === e.target.value))}
          >
            {crmData.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button onClick={runAnalysis} disabled={isAnalyzing} icon={<SafeIcon name="Zap" />}>
            {isAnalyzing ? "Analyserer..." : "Kjør AI-Diagnose"}
          </Button>
        </div>
      </div>

      {!diagnosis && !isAnalyzing && (
        <Card className="bg-indigo-900 border-none text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -mr-20 -mt-20"></div>
          <CardContent className="p-12 text-center relative z-10">
            <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-md">
              <SafeIcon name="Activity" className="text-4xl text-indigo-300" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Klar for analyse</h2>
            <p className="text-indigo-100 max-w-lg mx-auto mb-8">
              Velg et prosjekt med fullførte kartlegginger for å generere en diagnostisk rapport med AI-anbefalinger.
            </p>
            <Button variant="secondary" onClick={runAnalysis} className="bg-white text-indigo-900 hover:bg-indigo-50">
              Start Diagnostisering
            </Button>
          </CardContent>
        </Card>
      )}

      {isAnalyzing && (
        <div className="py-20 text-center space-y-4">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-500 font-medium animate-pulse">Aggregerer data og kjører anbefalingsmotor...</p>
        </div>
      )}

      <AnimatePresence>
        {diagnosis && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Left Column: Summary & Interventions */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader 
                  title="Executive Summary" 
                  subtitle={`Basert på ${selectedProject?.name}`}
                  icon={<SafeIcon name="FileText" />}
                />
                <CardContent>
                  <p className="text-lg font-medium text-slate-800 leading-relaxed italic">
                    "{diagnosis.summary}"
                  </p>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {diagnosis.risks.map((risk, i) => (
                      <div key={i} className={`p-4 rounded-2xl border ${getRiskColor(risk.level)}`}>
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-[10px] font-black uppercase tracking-widest opacity-70">Risiko: {risk.area}</span>
                          <span className="font-bold text-sm">{risk.score}%</span>
                        </div>
                        <p className="text-xs font-bold leading-snug">{risk.impact}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader title="Anbefalte Tiltak" subtitle="Strategiske intervensjoner for ledelse og ansatte." />
                <CardContent className="space-y-4">
                  {diagnosis.interventions.map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 items-start">
                      <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <p className="text-sm font-medium text-slate-700">{item}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Workshops & Export */}
            <div className="space-y-6">
              <Card className="bg-slate-900 border-none text-white">
                <CardHeader title="Foreslåtte Workshops" subtitle="Direkte koblet til avvik" />
                <CardContent className="space-y-3">
                  {diagnosis.workshops.map((ws, i) => (
                    <div key={i} className="p-3 bg-white/10 rounded-xl flex items-center justify-between group hover:bg-white/20 transition-all cursor-pointer">
                      <div className="flex items-center gap-3">
                        <SafeIcon name="Users" className="text-indigo-400" />
                        <span className="text-xs font-bold">{ws}</span>
                      </div>
                      <SafeIcon name="ChevronRight" className="text-slate-500 group-hover:text-white" />
                    </div>
                  ))}
                  <Button variant="primary" className="w-full mt-4 bg-indigo-500 hover:bg-indigo-400">
                    Book alle sesjoner
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 space-y-4">
                  <h4 className="font-bold text-slate-900">Rapporteksport</h4>
                  <p className="text-xs text-slate-500">
                    Generer en fullstendig PDF-rapport med grafer, AI-analyse og tiltaksplan klar for presentasjon i ledergruppen.
                  </p>
                  <Button variant="secondary" className="w-full" icon={<SafeIcon name="Download" />}>
                    Last ned PDF Rapport
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-emerald-50 border-emerald-100">
                <CardContent className="p-4 flex items-start gap-3">
                  <SafeIcon name="CheckCircle" className="text-emerald-600 mt-1" />
                  <div>
                    <p className="text-xs font-bold text-emerald-900">Benchmark Suksess</p>
                    <p className="text-[10px] text-emerald-700 leading-relaxed">
                      Lignende tiltak i din bransje har redusert stress-score med gjennomsnittlig 18% over 6 måneder.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};